import { Component, Inject, OnInit, OnDestroy } from '@angular/core';
import Env from '@ridango/core/env';
import { Observable } from 'rxjs';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { NotificationFacade, RecoverPasswordFacade, StemRecover } from '@ridango/components';
import { TranslateService } from '@ngx-translate/core';
import { take, takeUntil } from 'rxjs/operators';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthParametersFacade } from '@ridango/components/lib/auth/+state/auth-parameters';
import { BaseComponent } from '@ridango/core';

@Component({
    selector: 'gl-password-recover-page-v2',
    templateUrl: './password-recover-page-v2.component.html',
    styleUrls: ['./password-recover-page-v2.component.scss']
})
export class PasswordRecoverPageV2Component extends BaseComponent implements OnInit, OnDestroy {
    public loading$: Observable<boolean> = this.recoverPassword.loading$;
    public emailForm: FormGroup;
    public email: string;

    private callback: string;
    private locale: string;

    constructor(
        @Inject('ENV') public env: Env,
        private recoverPassword: RecoverPasswordFacade,
        private authParams: AuthParametersFacade,
        private notification: NotificationFacade,
        private translate: TranslateService,
        private route: ActivatedRoute,
        private router: Router
    ) {
        super();
        this.recoverPassword.resetStep();

        this.recoverPassword.error$
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe((error) => error && this.handleError(error));

        this.recoverPassword.step$.pipe(takeUntil(this.componentDestroyed$)).subscribe((step) => {
            if (step === StemRecover.SendEmail) {
                this.notification.info(this.translate.instant('PASSWORD_REQUEST') + ` ${this.email}`);
                this.router.navigate([this.translate.currentLang, 'login']);
            }
        });

        this.emailForm = new FormGroup({
            email: new FormControl('', { validators: [Validators.required, Validators.email] })
        });
    }

    ngOnInit() {
        this.locale = this.translate.currentLang;

        if (this.env.SIGN_ON_PAGE) {
            this.handleCallbackURL();
        }
    }

    public get isRequired() {
        return this.emailForm.controls.email.errors['required'];
    }

    public get isTouched(): boolean {
        return !!(this.emailForm.controls.email.touched && this.emailForm.controls.email.errors);
    }

    public handleBackClicked() {
        window.history.back();
    }

    public submit() {
        this.notification.removeAll();
        if (this.emailForm.valid) {
            this.email = this.emailForm.value.email;
            this.recoverPassword.submitEmail(this.email, this.locale, this.callback);
        } else {
            this.emailForm.controls.email.markAsTouched();
        }
    }

    public handleError(e) {
        let errorMessage = e.error ? e.error.error : 'INVALID_INPUT';
        if (e.status === 0) errorMessage = 'INTERNET_CONNECTION';
        this.notification.error(this.translate.instant(errorMessage));
    }

    private handleCallbackURL() {
        this.authParams.callback$.pipe(take(2)).subscribe((callback) => {
            if (callback) {
                this.callback = callback;
            }
        });

        this.route.queryParams.pipe(takeUntil(this.componentDestroyed$)).subscribe((params) => {
            if (params.callback) {
                this.authParams.setCallback(params.callback);
            }
        });
    }
}
